/**
 * HVILKET IKON ET VARSEL FÅR (rettelse B5, koi4 funn 9).
 *
 * =================================================================================================
 * FEMTI RADER MED DEN SAMME SETNINGSFORMEN LESES IKKE.
 *
 * Varselfanen henter en hel side. Man leser den ikke ovenfra og ned — man leter etter DEN ENE, og
 * typen er det man kjenner den igjen på. Et ikon er det billigste skillet som finnes: øyet finner
 * klokka blant konvoluttene uten å lese et ord.
 *
 * Kartet står her og ikke i komponenten fordi klokka, oversiktspanelet og fanen skal si det samme
 * om den samme raden. Tre kopier ville vært tre utgaver av «hva slags varsel er dette».
 *
 * =================================================================================================
 * `FALLBACK` ER EN EKTE VERDI, OG DERFOR FINNES `notification-icon.spec.ts`.
 *
 * Et varsel med en ukjent type skal tegnes — en lytter som er sendt før ikonet sitt er ikke en
 * grunn til en tom rad. Men den stille utgaven av det er at ALLE nye typer ser like ut, og at
 * ingen merker det. Spec-fila leser `@EventListener(...)` ut av API-ets lyttere og krever en rad i
 * dette kartet for hver av dem.
 */
import type { IconName } from '@/components/ui/icons';

/** Det generiske varselikonet — det klokka i toppen bruker for seg selv. */
export const FALLBACK_NOTIFICATION_ICON: IconName = 'alert';

/**
 * Hendelsesnavnet varselet ble skrevet av (`notifications.type`), ikke i18n-nøkkelen: typen er
 * maskinlesbar og stabil, nøkkelen er tekst som kan skrives om.
 */
export const NOTIFICATION_ICONS: Readonly<Record<string, IconName>> = {
  /* Oppgaver. En frist er en klokke, en fullført oppgave er et flueben. */
  'task.assigned': 'tasks',
  'task.co_assigned': 'tasks',
  'task.completed': 'check',
  'task.rescheduled': 'calendar',
  'task.status_changed': 'edit',
  'task.mentioned': 'profile',
  'task.commented': 'reply',
  'task.due_soon': 'clock',
  'task.overdue': 'clock',
  /* Sak 216.9: oppgaven gitt fra et hus man ikke er med i. */
  'task.given': 'tasks',
  /* 219.4: artisten fullførte en oppgave du ga. */
  'task.given_completed': 'check',

  /* Meldinger og møter. */
  'message.posted': 'mail',
  'event.invited': 'calendar',
  'event.changed': 'calendar',
  /* 239.46: noen svarte på en invitasjon arrangøren sendte. */
  'event.responded': 'reply',

  /* Bookingkjeden, som går over to hus — se `booking-notification.listener.ts`. */
  'booking_enquiry.received': 'booking',
  'booking_enquiry.answered': 'reply',
  'booking_enquiry.party_shared': 'contacts',
  'booking_enquiry.party_changed': 'contacts',
  /* Sak 239.2: arrangøren endret en godtatt avtale. */
  'booking_agreement.changed': 'reply',
  /* A8: den andre parten endret den godtatte avtalen før signering. */
  'deal_sheet.amended': 'reply',
  /* F22: arrangøren godtok byråets motbud. */
  'booking_counter.accepted': 'check',
  'contract_proposal.sent': 'documents',
  'contract_proposal.decided': 'check',
  'shared_advance.opened': 'documents',

  /* Vakter (201.14): satt på, eller tiden flyttet. */
  'shift.assigned': 'clock',
  'shift.changed': 'calendar',
  /* QA16 297.3–297.4: arrangementet du står på vakt på er endret; påmelding og bytte. */
  'arrangement.crew_changed': 'calendar',
  'shift_request.opened': 'clock',
  'shift_request.accepted': 'clock',
  'shift_request.decided': 'check',

  /* Spillested: huset endret salens spesifikasjon — arrangøren og byrået får vite det. */
  'venue_room.spec_changed': 'venue',

  /* Datoforespørselen (agent C): huset hører om forespørslene, arrangøren om hvert svar. */
  'venue_enquiry.received': 'venue',
  'venue_enquiry.withdrawn': 'cancelled',
  'venue_enquiry.concert_moved': 'calendar',
  'date_request.held': 'clock',
  'date_request.promoted': 'chevronUp',
  'date_request.expired': 'clock',
  'date_request.confirmed': 'check',
  'date_request.declined': 'cancelled',
  'date_request.released': 'cancelled',
  'date_request.superseded': 'cancelled',
  'date_request.night_moved': 'calendar',
  'date_request.night_cancelled': 'cancelled',
  /* Atles svar 29.09 (V1): «Forleng hold», og en ny melding i samtalen om datoen. */
  'date_request.hold_extended': 'clock',
  'date_request.message_posted': 'reply',
  'venue_enquiry.message_posted': 'reply',
  /* QA15-S B: arrangementet laget av et signert bud, leid ut, avlyst og gjenopprettet. */
  'venue_night.auto_created': 'venue',
  'venue_night.leased': 'venue',
  'arrangement.cancelled': 'cancelled',
  'arrangement.reinstated': 'check',
  /* QA15-S K1: arrangementet er flyttet — byrå, artist og management får vite det. */
  'arrangement.moved': 'calendar',

  /* Sak 232, T4: noe er delt med deg, delingen er endret, eller den er avsluttet. */
  'share.created': 'link',
  'share.updated': 'edit',
  'share.revoked': 'cancelled',
  'share.expired': 'clock',
  /* 284.2 (1145): manageren mistet tilgangen; de hen la til, beholder sin. */
  'artist_access.giver_lost': 'warning',
  /* F5 (runde 4. oktober, pakke 2): mottakeren åpnet delingen, eller bekreftet mottatt. */
  'share.opened': 'reveal',
  'share.confirmed': 'check',

  /* 239.21 (0541): en organisasjon vil samarbeide, og svaret. */
  'organization_partner.requested': 'link',
  'organization_partner.accepted': 'check',
  'organization_partner.declined': 'cancelled',

  /* Jobb 4 (0493): virusskanningen stoppet en fil. */
  'attachment.infected': 'warning',

  /* F21 (pakke 3): et trukket bud, og artisten satt på et reisefølge. */
  'booking_bid.withdrawn': 'cancelled',
  'booking_party.member_added': 'contacts',
  /* 253.16 (0934): en kontaktforespørsel til meg. Manglet, og testen under var rød (sett av R1). */
  'person_connection.requested': 'contacts',

  /* 245.5 (runde 5. oktober): arrangøren godtok eller avslo budet. */
  'booking_bid.accepted': 'check',
  'booking_bid.declined': 'cancelled',
  /* 253.80: budet ble godtatt, men årets bekreftede pris står. */
  'booking_bid.price_kept': 'warning',

  /* F62 og F59 (pakke 3): medlemskapet endret, og organisasjonen fryst eller åpnet. */
  'organization.role_changed': 'profile',
  'organization.member_deactivated': 'warning',
  'organization.member_reactivated': 'check',
  'organization.frozen': 'warning',
  'organization.unfrozen': 'check',
  /* 245.7: fjernet fra organisasjonen. */
  'organization.member_removed': 'warning',

  /* Eksporten (docs/EKSPORT.md §8): klar til nedlasting, eller feilet. */
  'export.ready': 'download',
  'export.failed': 'warning',
};

export function notificationIcon(type: string): IconName {
  return NOTIFICATION_ICONS[type] ?? FALLBACK_NOTIFICATION_ICON;
}
